import React from 'react'
import {Typography} from '@mui/material'
import moment from 'moment'
import {ColumnDef} from 'src/components-new/data/DataTable'
import StatusPill from 'src/components-new/primitives/StatusPill'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'

export interface ExportRow {
  _id: string
  filename?: string
  fileServiceName?: string
  createdAt?: string
  status?: string
  [key: string]: any
}

interface ExportColumnConfig {
  id: string
  label: string
}

// Same raw-value -> label mapping as the legacy page (src/pages/export/Export.tsx's
// getStatusLabel). Kept here (duplicated, not imported) so both stacks show identical
// Italian labels without pages-new reaching into src/pages/**.
export function getStatusLabel(status?: string): string {
  switch (status) {
    case 'created':
      return 'Creato'
    case 'processing':
      return 'In elaborazione'
    case 'error':
      return 'Errore'
    default:
      return status ?? '—'
  }
}

function getStatusColor(status?: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'created':
      return 'success'
    case 'processing':
      return 'warning'
    case 'error':
      return 'error'
    default:
      return 'default'
  }
}

// Same default columns as the legacy page's DEFAULT_COLUMNS — kept here as the fallback
// so both stacks show identical Italian labels when the PSP hasn't overridden
// `exportConfigurations.columnOptions`.
const DEFAULT_COLUMNS: ExportColumnConfig[] = [
  {id: 'filename', label: 'FILE'},
  {id: 'createdAt', label: 'DATA CREAZIONE'},
  {id: 'status', label: 'STATO'},
]

function renderCell(id: string) {
  return (row: ExportRow): React.ReactNode => {
    if (id === 'filename') {
      return <Typography variant="body2">{row.filename ?? '—'}</Typography>
    }
    if (id === 'createdAt') {
      return (
        <Typography variant="body2">
          {row.createdAt ? moment(row.createdAt).format('ddd DD MMM YYYY HH:mm') : '—'}
        </Typography>
      )
    }
    if (id === 'status') {
      return <StatusPill label={getStatusLabel(row.status)} color={getStatusColor(row.status)} />
    }
    return <Typography variant="body2">{(row as any)[id] ?? '—'}</Typography>
  }
}

interface BuildColumnsOptions {
  columnConfig?: ExportColumnConfig[]
  onDownload: (row: ExportRow) => void
  downloadingId?: string
}

/**
 * Columns are PSP-driven (`exportConfigurations.columnOptions`), falling back to the same
 * default set as the legacy page — mirrors the pattern in documents/columns.tsx /
 * biologicalSamplesColumns.tsx. `filename` renders as the mobile card header and `status`
 * as its top-right badge, same convention as PatientsListNew / BiologicalSamplesListNew.
 * The row-level "Scarica" action (raw `status === 'created'`, NOT the Italian label — the
 * legacy page's `row.status === 'Creato'` check compared against its own already-translated
 * rows, which this port avoids by keeping `status` raw until render time) is appended last,
 * matching documents/columns.tsx's actionsCol-always-last convention. There is no delete
 * action, matching the legacy page.
 */
export function buildColumns({columnConfig, onDownload, downloadingId}: BuildColumnsOptions): ColumnDef<ExportRow>[] {
  const config = columnConfig && columnConfig.length > 0 ? columnConfig : DEFAULT_COLUMNS

  const dataCols: ColumnDef<ExportRow>[] = config.map(col => ({
    key: col.id,
    header: col.label,
    mobileCardHeader: col.id === 'filename',
    mobileCardBadge: col.id === 'status',
    render: renderCell(col.id),
  }))

  const actionsCol: ColumnDef<ExportRow> = {
    key: 'actions',
    header: '',
    width: 64,
    isActionColumn: true,
    render: (row) => {
      if (row.status !== 'created') return null
      return (
        <ActionIconButton
          disabled={downloadingId === row._id}
          onClick={(e) => { e.stopPropagation(); onDownload(row) }}
          tooltip="Scarica"
          variant="download"
        />
      )
    },
  }

  return [...dataCols, actionsCol]
}
